import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import Navbar from "../Components/Navbar/navbar";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db } from "../Config/firebase";
import { COLLECTION_CAPSULAS } from "./compactacaoModel";
import { consultaTenantAtual , aplicarTenantAtual} from "../Utils/tenant";

const grupos = [
  { tipo: "higroscopica", titulo: "UMIDADE HIGROSCOPICA", cor: "#dcfce7" },
  { tipo: "limite_liquidez", titulo: "CAPSULAS LL", cor: "#fef3c7" },
  { tipo: "limite_plasticidade", titulo: "TAMPAS LP", cor: "#fef9c3" },
];

function normalizar(valor) {
  return String(valor || "").toLowerCase();
}

function proximoNumero(lista) {
  const maior = lista.reduce((max, item) => {
    const n = Number(String(item.numero || "").replace(/\D/g, ""));
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return String(maior + 1).padStart(2, "0");
}

function ordenar(lista) {
  return [...lista].sort((a, b) =>
    String(a.numero || "").localeCompare(String(b.numero || ""), "pt-BR", {
      numeric: true,
    })
  );
}

export default function CadastroTabelaCapsulas() {
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const [listas, setListas] = useState({
    higroscopica: [],
    limite_liquidez: [],
    limite_plasticidade: [],
  });
  const [filtro, setFiltro] = useState("");
  const [tipoQtd, setTipoQtd] = useState("higroscopica");
  const [openQtd, setOpenQtd] = useState(false);
  const [qtdAdicionar, setQtdAdicionar] = useState("");

  useEffect(() => {
    carregar();
  }, []);

  async function carregar() {
    const snap = await getDocs(
      consultaTenantAtual(query(collection(db, COLLECTION_CAPSULAS), orderBy("numero", "asc")))
    );

    const agrupado = {
      higroscopica: [],
      limite_liquidez: [],
      limite_plasticidade: [],
    };

    snap.docs.forEach((item) => {
      const dados = { id: item.id, ...item.data() };
      const tipo = dados.tipo || "higroscopica";
      if (agrupado[tipo]) agrupado[tipo].push(dados);
    });

    grupos.forEach(({ tipo }) => {
      agrupado[tipo] = ordenar(agrupado[tipo]);
    });

    setListas(agrupado);
  }

  function alterarLinha(tipo, index, campo, valor) {
    setListas((atual) => ({
      ...atual,
      [tipo]: atual[tipo].map((item, i) =>
        i === index
          ? {
              ...item,
              tipo,
              [campo]: valor,
              ...(tipo === "limite_plasticidade" && campo === "pesoTampa"
                ? { pesoCapsula: "" }
                : {}),
              ...(tipo === "limite_liquidez" && campo === "pesoCapsula"
                ? { pesoTampa: "" }
                : {}),
            }
          : item
      ),
    }));
  }

  function abrirAdicionar(tipo) {
    setTipoQtd(tipo);
    setQtdAdicionar("");
    setOpenQtd(true);
  }

  function acrescentarCapsulas() {
    const qtd = Number(qtdAdicionar);
    if (!qtd || qtd <= 0) return;

    setListas((atual) => {
      let numeroAtual = Number(proximoNumero(atual[tipoQtd]));
      const novas = Array.from({ length: qtd }, () => {
        const numero = String(numeroAtual).padStart(2, "0");
        numeroAtual += 1;
        return {
          numero,
          tipo: tipoQtd,
          pesoCapsula: "",
          pesoTampa: "",
          observacao: "",
          ativo: true,
        };
      });

      return {
        ...atual,
        [tipoQtd]: [...atual[tipoQtd], ...novas],
      };
    });

    setOpenQtd(false);
  }

  async function salvarTodos() {
    const todas = grupos.flatMap(({ tipo }) => listas[tipo] || []);

    for (const item of todas) {
      const dados = {
        numero: item.numero || "",
        tipo: item.tipo || "higroscopica",
        pesoCapsula:
          item.tipo === "limite_plasticidade" ? "" : item.pesoCapsula || "",
        pesoTampa:
          item.tipo === "limite_plasticidade" ? item.pesoTampa || "" : "",
        observacao: item.observacao || "",
        ativo: item.ativo ?? true,
        atualizadoEm: new Date(),
      };

      if (item.id) {
        await updateDoc(doc(db, COLLECTION_CAPSULAS, item.id), dados);
      } else {
        await addDoc(collection(db, COLLECTION_CAPSULAS), aplicarTenantAtual({
          ...dados,
          criadoEm: new Date(),
        }));
      }
    }

    await carregar();
  }

  const listasFiltradas = useMemo(() => {
    const termo = normalizar(filtro);
    if (!termo) return listas;

    return Object.fromEntries(
      grupos.map(({ tipo }) => [
        tipo,
        (listas[tipo] || []).filter((item) =>
          [item.numero, item.pesoCapsula, item.pesoTampa, item.observacao].some((campo) =>
            contemTextoPesquisa(normalizar(campo), termo)
          )
        ),
      ])
    );
  }, [listas, filtro]);

  function QuadroCapsulas({ grupo }) {
    const linhas = listasFiltradas[grupo.tipo] || [];
    const campoPeso = grupo.tipo === "limite_plasticidade" ? "pesoTampa" : "pesoCapsula";
    return (
      <Paper sx={{ border: "1px solid #111827", borderRadius: 0, boxShadow: "none" }}>
        <Table
          size="small"
          sx={{
            "& td, & th": {
              border: "1px solid #111827",
              color: "#000",
              fontSize: 12,
              p: "3px 5px",
              textAlign: "center",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell colSpan={3} sx={{ fontWeight: 800 }}>
                {grupo.titulo}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Nº</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>PESO</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>HISTÓRICO</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.map((item, index) => {
              const indexReal = (listas[grupo.tipo] || []).findIndex((x) =>
                item.id ? x.id === item.id : x.numero === item.numero
              );
              return (
                <TableRow
                  key={item.id || `${grupo.tipo}-${item.numero}`}
                  sx={{ backgroundColor: index % 2 === 0 ? grupo.cor : "#ffffff" }}
                >
                  <TableCell sx={{ width: 70 }}>
                    <TextField
                      size="small"
                      value={item.numero || ""}
                      onChange={(e) =>
                        alterarLinha(grupo.tipo, indexReal, "numero", e.target.value)
                      }
                      inputProps={{ style: { textAlign: "center", padding: 2, fontSize: 12 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 95 }}>
                    <TextField
                      size="small"
                      value={item[campoPeso] || ""}
                      onChange={(e) =>
                        alterarLinha(grupo.tipo, indexReal, campoPeso, e.target.value)
                      }
                      inputProps={{ style: { textAlign: "center", padding: 2, fontSize: 12 } }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 62 }}>
                    <HistoricoCadastro colecao={COLLECTION_CAPSULAS} documentoId={item.id} registro={item} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Box sx={{ p: 1 }}>
          <Button size="small" variant="outlined" onClick={() => abrirAdicionar(grupo.tipo)}>
            Acrescentar
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Navbar />

      <Box sx={{ mt: "10px", textAlign: "center" }}>
        <Typography variant="h6" fontWeight={700}>
          Nº E PESO DE CÁPSULAS
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
          <Button variant="contained" color="success" onClick={() => executarSalvamento(salvarTodos)} disabled={salvandoCadastro}>
            {salvandoCadastro ? "Salvando..." : "Salvar alterações"}
          </Button>

          <TextField
            size="small"
            label="Pesquisar"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            sx={{ flex: 1 }}
          />
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
            gap: 4,
            alignItems: "start",
          }}
        >
          {grupos.map((grupo) => (
            <QuadroCapsulas key={grupo.tipo} grupo={grupo} />
          ))}
        </Box>
      </Box>

      <Dialog open={openQtd} onClose={() => setOpenQtd(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Acrescentar cápsulas</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Quantidade"
            type="number"
            value={qtdAdicionar}
            onChange={(e) => setQtdAdicionar(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQtd(false)}>Cancelar</Button>
          <Button variant="contained" onClick={acrescentarCapsulas}>
            Acrescentar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
